import { useState, useEffect } from 'react';
import { api } from '../services/api';
import { Loading } from '../components/Loading';
import type { User } from '../services/types';

type FilterType = 'all' | 'pending' | 'approved' | 'rejected';

const filterLabels: Record<FilterType, string> = {
  pending: '待審核',
  approved: '已通過',
  rejected: '已拒絕',
  all: '全部',
};

const statusConfig: Record<User['status'], { badge: string; label: string }> = {
  approved: { badge: 'badge-success', label: '已通過' },
  pending: { badge: 'badge-warning', label: '待審核' },
  rejected: { badge: 'badge-danger', label: '已拒絕' },
};

export function AdminPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('pending');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, [filter]); // loadUsers is not stable, but filter change should trigger reload

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data =
        filter === 'pending'
          ? await api.getPendingUsers()
          : await api.getAllUsers();

      const filtered =
        filter === 'all' || filter === 'pending'
          ? data
          : data.filter((u) => u.status === filter);

      setUsers(filtered);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId: string) => {
    setActionLoading(userId);
    try {
      await api.approveUser(userId);
      loadUsers();
    } catch (error) {
      console.error('Failed to approve user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId: string) => {
    if (!confirm('確定要拒絕此用戶嗎？此操作會永久禁止該用戶登入。')) {
      return;
    }
    setActionLoading(userId);
    try {
      await api.rejectUser(userId);
      loadUsers();
    } catch (error) {
      console.error('Failed to reject user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnreject = async (userId: string) => {
    setActionLoading(userId);
    try {
      await api.unrejectUser(userId);
      loadUsers();
    } catch (error) {
      console.error('Failed to unreject user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handlePromote = async (userId: string) => {
    if (!confirm('確定要將此用戶升級為管理員嗎？')) {
      return;
    }
    setActionLoading(userId);
    try {
      await api.promoteUser(userId);
      loadUsers();
    } catch (error) {
      console.error('Failed to promote user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <Loading text="載入用戶列表..." />;

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        {/* Header */}
        <header className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="heading-display">用戶管理</h1>
              <p className="body-base mt-2">管理平台用戶權限與審核</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {(['pending', 'approved', 'rejected', 'all'] as FilterType[]).map(
                (f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`btn btn-sm ${
                      filter === f ? 'btn-primary' : 'btn-ghost'
                    }`}
                  >
                    {filterLabels[f]}
                  </button>
                )
              )}
            </div>
          </div>
        </header>

        {/* Users Table */}
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 dark:bg-neutral-800/50 border-b border-neutral-100 dark:border-neutral-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                    用戶
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                    狀態
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                    角色
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {users.map((user) => {
                  const config = statusConfig[user.status];
                  return (
                    <tr key={user.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/30 transition-colors">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          {user.avatar_url ? (
                            <img
                              src={user.avatar_url}
                              alt=""
                              className="w-10 h-10 rounded-full ring-2 ring-neutral-100 dark:ring-neutral-700"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white font-medium">
                              {user.name?.[0] || 'U'}
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-neutral-900 dark:text-neutral-50">
                              {user.name}
                            </div>
                            <div className="text-sm text-neutral-500 dark:text-neutral-400">
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`badge ${config.badge}`}>
                          {config.label}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={
                            user.role === 'admin'
                              ? 'font-medium text-primary-600 dark:text-primary-400'
                              : 'text-neutral-600 dark:text-neutral-400'
                          }
                        >
                          {user.role === 'admin' ? '管理員' : '一般用戶'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex gap-2 flex-wrap">
                          {user.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApprove(user.id)}
                                disabled={actionLoading === user.id}
                                className="btn btn-success btn-sm"
                              >
                                通過
                              </button>
                              <button
                                onClick={() => handleReject(user.id)}
                                disabled={actionLoading === user.id}
                                className="btn btn-destructive btn-sm"
                              >
                                拒絕
                              </button>
                            </>
                          )}
                          {user.status === 'approved' && user.role !== 'admin' && (
                            <button
                              onClick={() => handlePromote(user.id)}
                              disabled={actionLoading === user.id}
                              className="btn btn-outline btn-sm"
                            >
                              升級管理員
                            </button>
                          )}
                          {user.status === 'rejected' && (
                            <button
                              onClick={() => handleUnreject(user.id)}
                              disabled={actionLoading === user.id}
                              className="btn btn-ghost btn-sm text-warning-600 dark:text-warning-400"
                            >
                              解除拒絕
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {users.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 mb-4 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                <svg className="w-8 h-8 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <p className="body-base">
                {filter === 'pending' && '沒有待審核的用戶'}
                {filter === 'approved' && '沒有已通過的用戶'}
                {filter === 'rejected' && '沒有已拒絕的用戶'}
                {filter === 'all' && '沒有用戶'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
